import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type PSPSNode = {
  id: string;
  type: 'psps';
  data: {
    type: 'persona' | 'situation' | 'pain' | 'solution';
    id: number;
    label: string;
    description: string;
  };
};

export type PSPSEdge = [string, string];

export type PSPSReport = {
  title: string;
  subtitle: string;
  nodes: PSPSNode[];
  edges: PSPSEdge[];
};

type UsePSPSReportResult = {
  data: PSPSReport | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};

async function fetchPSPSGraph(
  organisationId: number,
  projectId: number
): Promise<PSPSReport | null> {
  const { data: project, error: projectErr } = await supabase
    .from('project')
    .select('project_id, name, description')
    .eq('organisation_id', organisationId)
    .eq('project_id', projectId)
    .maybeSingle();

  if (projectErr) throw projectErr;
  if (!project) return null;

  const { data: psLinks, error: psErr } = await supabase
    .from('link_persona_situation')
    .select(
      'persona_situation_id, persona:persona_id(persona_id, name, description), situation:situation_id(situation_id, name, description)'
    )
    .eq('project_id', projectId)
    .eq('organisation_id', organisationId);

  if (psErr) throw psErr;
  const psLinksData = psLinks ?? [];

  const psIds = psLinksData.map((l) => l.persona_situation_id);

  type PainRow = {
    persona_situation_id: number;
    pain: { pain_id: number; name: string; description: string | null };
  };

  let painRows: PainRow[] = [];

  if (psIds.length > 0) {
    const { data: painData, error: painErr } = await supabase
      .from('link_persona_situation_pain')
      .select('persona_situation_id, pain:pain_id(pain_id, name, description)')
      .in('persona_situation_id', psIds)
      .eq('organisation_id', organisationId);

    if (painErr) throw painErr;
    painRows = (painData ?? []) as unknown as PainRow[];
  }

  const painsByPs: Record<number, Array<{ pain_id: number; name: string; description: string | null }>> = {};
  const allPainIds: number[] = [];

  for (const row of painRows) {
    if (!painsByPs[row.persona_situation_id]) {
      painsByPs[row.persona_situation_id] = [];
    }
    painsByPs[row.persona_situation_id].push(row.pain);
    allPainIds.push(row.pain.pain_id);
  }

  type SolRow = {
    pain_id: number;
    solution: { solution_id: number; name: string; description: string | null };
  };

  let solRows: SolRow[] = [];

  if (allPainIds.length > 0) {
    const { data: solData, error: solErr } = await supabase
      .from('link_solution_pain')
      .select('pain_id, solution:solution_id(solution_id, name, description)')
      .in('pain_id', allPainIds)
      .eq('organisation_id', organisationId);

    if (solErr) throw solErr;
    solRows = (solData ?? []) as unknown as SolRow[];
  }

  const solutionsByPain: Record<number, Array<{ solution_id: number; name: string; description: string | null }>> = {};
  for (const row of solRows) {
    if (!solutionsByPain[row.pain_id]) {
      solutionsByPain[row.pain_id] = [];
    }
    solutionsByPain[row.pain_id].push(row.solution);
  }

  const nodes: PSPSNode[] = [];
  const edges: PSPSEdge[] = [];
  const seenNodes = new Set<string>();

  function addNode(
    nodeId: string,
    entityType: 'persona' | 'situation' | 'pain' | 'solution',
    entity: { [key: string]: unknown },
    pkField: string
  ) {
    if (seenNodes.has(nodeId)) return;
    seenNodes.add(nodeId);
    nodes.push({
      id: nodeId,
      type: 'psps',
      data: {
        type: entityType,
        id: entity[pkField] as number,
        label: (entity.name as string) ?? '',
        description: (entity.description as string) ?? '',
      },
    });
  }

  type LinkRow = {
    persona_situation_id: number;
    persona: { persona_id: number; name: string; description: string | null };
    situation: { situation_id: number; name: string; description: string | null };
  };

  const links = psLinksData as unknown as LinkRow[];

  for (const link of links) {
    const persona = link.persona;
    const situation = link.situation;
    const perId = `per${persona.persona_id}`;
    const sitId = `sit${situation.situation_id}`;

    addNode(perId, 'persona', persona, 'persona_id');
    addNode(sitId, 'situation', situation, 'situation_id');
    edges.push([perId, sitId]);

    for (const pain of painsByPs[link.persona_situation_id] ?? []) {
      const painIdStr = `pain${pain.pain_id}`;
      addNode(painIdStr, 'pain', pain, 'pain_id');
      edges.push([sitId, painIdStr]);

      for (const sol of solutionsByPain[pain.pain_id] ?? []) {
        const solIdStr = `sol${sol.solution_id}`;
        addNode(solIdStr, 'solution', sol, 'solution_id');
        edges.push([painIdStr, solIdStr]);
      }
    }
  }

  const edgeSet = new Set<string>();
  const uniqueEdges: PSPSEdge[] = [];
  for (const edge of edges) {
    const key = `${edge[0]}-${edge[1]}`;
    if (!edgeSet.has(key)) {
      edgeSet.add(key);
      uniqueEdges.push(edge);
    }
  }

  const nPersonas = nodes.filter((n) => n.data.type === 'persona').length;
  const nSituations = nodes.filter((n) => n.data.type === 'situation').length;
  const nPains = nodes.filter((n) => n.data.type === 'pain').length;
  const nSolutions = nodes.filter((n) => n.data.type === 'solution').length;

  return {
    title: project.name ?? '',
    subtitle: `${
      nPersonas
    } PERSONA${nPersonas !== 1 ? 'S' : ''} · ${
      nSituations
    } SITUATION${nSituations !== 1 ? 'S' : ''} · ${
      nPains
    } PAIN${nPains !== 1 ? 'S' : ''} · ${
      nSolutions
    } SOLUTION${nSolutions !== 1 ? 'S' : ''}`,
    nodes,
    edges: uniqueEdges,
  };
}

export function usePSPSReport(
  organisationId: number | null,
  projectId: number | null
): UsePSPSReportResult {
  const [data, setData] = useState<PSPSReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (organisationId == null || projectId == null) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchPSPSGraph(organisationId, projectId);
      setData(result);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, projectId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}
